// Rename all files to a numbered pattern.
// "my_gallery_###" → my_gallery_001.jpg, my_gallery_002.mp4, … in current grid
// order. The run of # sets the zero-padding width. Extensions are kept but
// cleaned up: lowercased, jpeg → jpg.
//
// The whole run executes inside app.js's withExclusiveOp, two-phase
// (everything to a unique temp name first, then temp → final) so renames
// never collide with each other; per-file moves reuse tree.js's moveFile and
// the sort file is written explicitly + awaited inside the scope (never the
// deferred scheduleSave). See the comments in renameAll below.
import { renameThumb } from './thumbs.js';
import { moveFile } from './tree.js';
import { settings } from './settings.js';
import { icon } from './util.js';

export function initRename({ state, grid, setStatus, writeSortFile, resetHistory, resync, withExclusiveOp, isThumbBusy }) {
  const renameBtn = document.getElementById('renameBtn');
  const renameDlg = document.getElementById('renameDlg');
  const renameForm = document.getElementById('renameForm');
  const renameInput = document.getElementById('renameInput');
  const renameErr = document.getElementById('renameErr');
  const renameList = document.getElementById('renameList');
  const renameOk = document.getElementById('renameOk');

  renameBtn.onclick = () => {
    if (!state.dir || !grid.children.length) return;
    // renameAll can't start while a recursive thumbnail job is reading this
    // subtree — a move racing that job's open file readers can fail on a real
    // filesystem (see tree.js). Refuse here rather than let the dialog open
    // only to fail once submitted.
    if (isThumbBusy()) { setStatus('Waiting for thumbnail job — cancel it first', 'error'); return; }
    renameInput.value = settings.get('renamePattern');
    updateRenamePreview();
    grid.classList.add('no-scroll');
    renameDlg.showModal();
    renameInput.select();
  };
  // Fires on Esc, Cancel and submit alike.
  renameDlg.addEventListener('close', () => grid.classList.remove('no-scroll'));

  function patternError(pattern) {
    if (!/#/.test(pattern)) return 'The pattern must contain # placeholders, e.g. my_gallery_###';
    if (/[\/\\:*?"<>|]/.test(pattern)) return 'The pattern contains characters not allowed in file names.';
    if (pattern.startsWith('.')) return 'The pattern must not start with a dot — that would make the files hidden.';
    return null;
  }

  function buildTargets(pattern) {
    const width = pattern.match(/#+/)[0].length;
    return [...grid.children].map((c, i) =>
      pattern.replace(/#+/, String(i + 1).padStart(width, '0')) + cleanExt(c._item.name));
  }

  // Live old → new preview inside the dialog.
  function updateRenamePreview() {
    const pattern = renameInput.value;
    const err = patternError(pattern);
    renameErr.hidden = !err;
    renameErr.textContent = err || '';
    renameOk.disabled = !!err;
    renameList.replaceChildren();
    if (err) return;
    const targets = buildTargets(pattern);
    const frag = document.createDocumentFragment();
    [...grid.children].forEach((c, i) => {
      const row = document.createElement('div');
      row.className = 'rrow' + (targets[i] === c.dataset.name ? ' same' : '');
      const old = document.createElement('span');
      old.className = 'old'; old.textContent = c.dataset.name; old.title = c.dataset.name;
      const arr = document.createElement('span');
      arr.className = 'arr'; arr.innerHTML = icon('arrow-right');
      const nw = document.createElement('span');
      nw.className = 'new'; nw.textContent = targets[i]; nw.title = targets[i];
      row.append(old, arr, nw);
      frag.appendChild(row);
    });
    renameList.appendChild(frag);
  }
  renameInput.addEventListener('input', updateRenamePreview);
  document.getElementById('renameCancel').onclick = () => renameDlg.close();
  renameForm.addEventListener('submit', (e) => {
    e.preventDefault();
    const pattern = renameInput.value;
    if (patternError(pattern)) return;
    renameDlg.close();
    settings.set('renamePattern', pattern);
    renameAll(buildTargets(pattern));
  });

  function cleanExt(name) {
    const m = /\.([^.]+)$/.exec(name);
    if (!m) return '';
    let ext = m[1].toLowerCase();
    if (ext === 'jpeg') ext = 'jpg';
    return '.' + ext;
  }

  async function renameAll(targets) {
    if (!state.dir || !grid.children.length) return;
    // Snapshot the directory: this runs inside withExclusiveOp (below), so
    // nothing else can repoint state.dir mid-run, but the primitive stays
    // correct on its own terms rather than trusting that.
    const dir = state.dir;
    const cells = [...grid.children];
    setStatus('Renaming…', 'saving');
    try {
      const renamed = await withExclusiveOp(async () => {
        // Refuse to overwrite files we don't manage (e.g. the ones behind the ⚠ icon).
        const existing = new Set();
        for await (const [name] of dir.entries()) existing.add(name);
        const ours = new Set(cells.map(c => c._item.name));
        const clash = targets.find(t => existing.has(t) && !ours.has(t));
        if (clash) {
          setStatus('Rename failed — “' + clash + '” already exists in this folder', 'error');
          return false;
        }

        // Two phases so renames never collide with each other (e.g. 002 → 001
        // while the old 001 still exists): first everything to a unique temp
        // name, then temp → final.
        const pending = [];
        for (let i = 0; i < cells.length; i++) {
          const item = cells[i]._item;
          if (targets[i] === item.name) continue;
          const tmp = 'mediasorter-tmp-' + Date.now() + '-' + i + cleanExt(item.name);
          item.handle = await moveFile(item.handle, dir, dir, tmp);
          item.name = tmp;
          pending.push(i);
        }
        for (const i of pending) {
          const item = cells[i]._item;
          item.handle = await moveFile(item.handle, dir, dir, targets[i]);
          const cell = cells[i], old = cell.dataset.name;
          item.name = targets[i];
          cell.dataset.name = item.name;
          cell.title = item.name;
          renameThumb(old, item.name, item.handle);
        }
        resetHistory();          // old snapshots refer to the old names
        await writeSortFile(dir, cells.map(c => c._item.name));
        return true;
      });
      if (renamed) setStatus('Renamed ✓');
    } catch (e) {
      console.error(e);
      setStatus('Rename failed', 'error');
      await resync();   // re-read the folder from disk, re-checking permission first
    }
  }
}
